// Dates, money, buyer families and small text helpers shared across pages.
import type { BudgetStatus, NoticeItem } from "../api/types";

const DAY = 864e5;
export const today = (): Date => new Date(new Date().toDateString());

/** Whole days from today to an ISO date; negative when past. */
export function days(iso: string | null | undefined): number | null {
  return iso ? Math.round((new Date(iso + "T00:00:00").getTime() - today().getTime()) / DAY) : null;
}

export function fmt(iso: string | null | undefined, options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" }): string {
  return iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", options) : "";
}

/** "Aug 23", or "Aug 23, 2027" when it isn't this year. */
export function shortDate(iso: string | null | undefined, options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" }): string {
  if (!iso) return "";
  const thisYear = iso.slice(0, 4) === String(today().getFullYear());
  return fmt(iso, thisYear ? options : { ...options, year: "numeric" });
}

export function dueText(n: Pick<NoticeItem, "deadline">): string {
  const d = days(n.deadline);
  if (d === null) return "No due date";
  if (d < 0) return "Closed";
  if (d === 0) return "Due today";
  if (d === 1) return "Due tomorrow";
  if (d < 7) return "Due " + fmt(n.deadline, { weekday: "long" });
  return "Due " + shortDate(n.deadline);
}

/** "2:44 this afternoon", "yesterday evening", "Tuesday", "Sep 12". */
export function when(iso: string | null | undefined): string {
  if (!iso) return "your last visit";
  const d = new Date(iso);
  const ago = Math.round((today().getTime() - new Date(d.toDateString()).getTime()) / DAY);
  const h = d.getHours();
  const part = h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
  const clock = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).replace(/ [AP]M$/, "");
  if (ago <= 0) return `${clock} this ${part}`;
  if (ago === 1) return `yesterday ${part}`;
  if (ago < 7) return d.toLocaleDateString("en-US", { weekday: "long" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function checkedText(iso: string | null): string {
  if (!iso) return "Sources have never been checked.";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 6e4);
  if (mins < 2) return "Sources were checked just now.";
  if (mins < 60) return `Sources were checked ${mins} minutes ago.`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `Sources were checked ${hrs} hour${hrs === 1 ? "" : "s"} ago.`;
  return `Sources were last checked ${d.toLocaleDateString("en-US", { weekday: "long" })} at ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}.`;
}

export const first = (name: string | null | undefined): string => (name ?? "").split(" ")[0] ?? "";
export const initials = (name: string | null | undefined): string =>
  (name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
const FACES = ["#A87B2E", "#3F7D6E", "#5B6FB0", "#9A4F6B", "#6C7A3B", "#3A6EA5", "#8A2A2A", "#55606E"];
export const faceColor = (id: number | string | null | undefined): string => FACES[(Number(id) || 0) % FACES.length]!;

// Buyer families: badge text and service color, so the buyer reads before the title.
const COCOM = /AFRICOM|SOUTHCOM|NORTHCOM|EUCOM|INDOPACOM|PACOM|CENTCOM|TRANSCOM|SPACECOM/;
const FAMILIES: [RegExp, string | ((u: string) => string), string][] = [
  [/\bNATO\b|NCIA|NSPA/, "NATO", "#1F4E8C"],
  [/SPECIAL OPERATIONS|SOCOM|\bSOF\b|NAVSPECWAR|NAVAL SPECIAL WARFARE|USASOC/, "SOF", "#5B4A2E"],
  [COCOM, (u) => u.match(COCOM)![0], "#3B3F58"],
  [/DEFENSE HEALTH|\bDHA\b|MEDICAL/, "DHA", "#7A2E3A"],
  [/NATIONAL INSTITUTES OF HEALTH|\bNIH\b/, "NIH", "#2C6B73"],
  [/MARINE CORPS|USMC/, "USMC", "#8A2A2A"],
  [/\bNAVY\b|NAVAL|NAVAIR|NAVSEA|NAVSUP|NAWC|NIWC|NSWC|\bONR\b|NAVWAR|NAVALX/, "NAVY", "#1F3A68"],
  [/SPACE FORCE|SPACE SYSTEMS COMMAND/, "USSF", "#2E3350"],
  [/AIR FORCE|\bUSAF\b|AFRL|AFWERX|ARCWERX|AIR EDUCATION|AIR RESERVE/, "USAF", "#3A6EA5"],
  [/\bARMY\b|DEVCOM|C5ISR|USACE|ENGINEER DISTRICT|FIRES|\bATEC\b/, "ARMY", "#4B5320"],
  [/DARPA|ADVANCED RESEARCH PROJECTS/, "DARPA", "#2B2F36"],
  [/DEFENSE LOGISTICS|\bDLA\b/, "DLA", "#55606E"],
  [/COAST GUARD|USCG/, "USCG", "#B3541E"],
  [/NASA|AERONAUTICS AND SPACE/, "NASA", "#1D5C9E"],
  [/HOMELAND|SECRET SERVICE|\bDHS\b/, "DHS", "#2F4F6F"],
  [/INNOVATION UNIT|\bDIU\b/, "DIU", "#30343B"],
  [/THREAT REDUCTION|DTRA/, "DTRA", "#3E4A3D"],
  [/U\.K\.|UNITED KINGDOM/, "UK", "#35457A"],
  [/DEFENSE|\bDOD\b|\bDOW\b|\bWAR\b/, "DoW", "#394B6B"],
];

export function family(agency: string | null | undefined): { label: string; color: string } {
  const u = (agency ?? "").toUpperCase();
  for (const [re, label, color] of FAMILIES) if (re.test(u)) return { label: typeof label === "function" ? label(u) : label, color };
  const words = (agency || "?")
    .replace(/[^A-Za-z ]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 2 && !/^(of|the|and|for)$/i.test(w));
  return { label: words.slice(0, 3).map((w) => w[0]).join("").toUpperCase() || "?", color: "#5C6470" };
}

/** SAM publishes an uppercase office path joined by dots; anything else is a plain name. */
export const orgParts = (a: string | null | undefined): string[] =>
  /^[A-Z][A-Z ,&'()-]{3,}\.[A-Z]/.test(a ?? "") ? (a ?? "").split(".").map((x) => x.trim()).filter(Boolean) : [a ?? ""];

/** The department's name as GovChew says it: the Department of War, whatever the source still calls it. */
const DEPARTMENT = /^(?:dept\.?|department) of (?:defense|war)$|^defense,? (?:dept\.?|department) of$/i;

/** A buyer's office path, readable: the full path for a tooltip, and a short one (department, then the last two
 * offices) for a line that has to fit. */
export function orgName(agency: string | null | undefined): { short: string; full: string } {
  const parts = orgParts(agency)
    .map(titleCase)
    .filter(Boolean)
    .map((p, i) => (i === 0 && DEPARTMENT.test(p) ? "DoW" : p));
  const short = parts.length > 3 ? [parts[0]!, ...parts.slice(-2)] : parts;
  return { short: short.join(" › "), full: parts.join(" › ").replace(/^DoW\b/, "Department of War") };
}

// Acronyms title casing would spoil: offices, programs and terms common in DoW and HHS records.
const ACRONYMS = new Set(
  (
    "acc afcec aflcmc afmc afrl afsoc ai amc amcom arpa aspr baa barda bpa c2 cbrn cdc cecom cio cso darpa dha " +
    "disa diu dla dod dow dtra eo fda gsa hcd hel hhs hii hq hqmc icbm idiq io isr mcce mcsc mlb msne nasa navair " +
    "navfac navsea navsup navwar nawc nen nga nih niwc nro nsa nswc nuwc opnav ota peo rfi rfp rfq sbir scms seta " +
    "smc soc socom sof ssc sttr tacom uas uav ugv us usa usaf usc usmc ussf ussocom usv uuv"
  ).split(" "),
);
// Short words without vowels that are abbreviations or titles, not acronyms.
const NOT_ACRONYMS = new Set("bldg blvd ctr dr ft hwy jr ln mgmt mgt mr mrs ms mt pkwy pt rd sr st svc svcs".split(" "));

const PLAIN_WORDS = /^(?:army|navy|base|only|site|east|west|north|south|draft|final|new)$/i;

/** Title case for all-caps text (SAM's office names, FPDS's vendors and titles), keeping acronyms: known ones,
 * words with digits (N2N6, S2ISR), short words without vowels (SCMS, PMW), and short parenthesized ones ((ISR)). */
export function titleCase(s: string): string {
  if (!(s.length >= 4 && s === s.toUpperCase() && /[A-Z]/.test(s))) return s;
  return s
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\b(Of|The|And|For|To|In|On|At|By|From|With)\b/g, (w, _m, i: number) => (i ? w.toLowerCase() : w))
    .replace(/\b[A-Za-z0-9]+\b/g, (w) => {
      const l = w.toLowerCase();
      if (ACRONYMS.has(l)) return w.toUpperCase();
      if (/\d/.test(w) && /[a-z]/i.test(w) && !/^\d+(?:st|nd|rd|th|s)$/i.test(w)) return w.toUpperCase();
      if (/^[a-z]{2,5}$/i.test(w) && !/[aeiouy]/i.test(w) && !NOT_ACRONYMS.has(l)) return w.toUpperCase();
      return w;
    })
    .replace(/\(([A-Za-z0-9&/-]{1,6})\)/g, (m, w: string) => (PLAIN_WORDS.test(w) ? m : `(${w.toUpperCase()})`));
}

/** A company as FPDS and SAM write it ("SYSTEMS & TECHNOLOGY RESEARCH LLC"), readable, suffixes kept short. */
export function companyName(s: string): string {
  return titleCase(s).replace(/\b(Llc|Lp|Llp|Jv|Pllc|Usa|Us|Ai|It|Ii|Iii|Iv)\b/g, (w) => w.toUpperCase());
}

/** Whether two spellings name the same company ("Booz Allen Hamilton Inc." and "BOOZ ALLEN HAMILTON"). */
export function sameCompany(a: string | null | undefined, b: string | null | undefined): boolean {
  const key = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9 ]+/g, " ")
      .replace(/\b(?:the|inc|incorporated|llc|l l c|corp|corporation|co|company|ltd|limited|lp|llp|plc|holding|holdings)\b/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  return !!a && !!b && key(a) === key(b);
}

export const host = (u: string): string => {
  try {
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

/** Links listed in a description's "Links and attachments:" block, then attachments. */
export function links(n: { description?: string; attachments?: string[] }): [string, string, string][] {
  const out: [string, string, string][] = [];
  const seen = new Set<string>();
  const block = (n.description ?? "").split("Links and attachments:\n")[1] ?? "";
  for (const line of block.split("\n")) {
    const m = line.match(/^- (.*): (https?:\/\/\S+)$/);
    if (m && !seen.has(m[2]!)) {
      seen.add(m[2]!);
      out.push([m[1]!.replace(/^View the full challenge and apply$/, "Challenge page and application"), m[2]!, host(m[2]!)]);
    }
  }
  let i = 0;
  for (const u of n.attachments ?? [])
    if (/^https?:\/\//i.test(u) && !seen.has(u)) {
      seen.add(u);
      i++;
      out.push([`Attachment ${i}`, u, host(u)]);
    }
  return out;
}

export const cleanDesc = (d: string | null | undefined): string =>
  (d ?? "")
    .split("\n\nLinks and attachments:")[0]!
    .replace(/\*\*/g, "")
    .replace(/\\([_*.\-#])/g, "$1")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/** $142.3M, $1.42B, $640K: budget exhibits state millions. */
export function money(m: number | null | undefined): string {
  if (m == null) return "—";
  const a = Math.abs(m);
  const sign = m < 0 ? "−" : "";
  if (a >= 1000) return `${sign}$${(a / 1000).toFixed(a >= 10000 ? 1 : 2)}B`;
  if (a >= 1) return `${sign}$${a.toFixed(a >= 100 ? 0 : 1)}M`;
  if (a > 0) return `${sign}$${Math.round(a * 1000)}K`;
  return "$0";
}

/** How a budget line is cited: "PE 0603790D8Z", "Line 1234", "SAG 1D4D" for an O&M sub-activity group, or for a
 * health agency's Congressional Justification, which numbers nothing, "CDC program" or "NIH budget activity". */
export function lineRef(b: { kind: string; number: string; project?: string; service?: string }, withProject = false): string {
  if (b.kind === "cj") return `${b.service ?? ""} ${b.project ? "program" : "budget activity"}`.trim();
  if (b.kind === "rdte") return `PE ${b.number}${withProject && b.project ? " · Project " + b.project : ""}`;
  if (b.kind === "om") return `SAG ${b.number}${withProject && b.project ? " · program" : ""}`;
  return `Line ${b.number}`;
}

export const fy = (year: number | string): string => "FY" + String(year).slice(2);

export function changeWords(b: { status: BudgetStatus; change: number | null }): { text: string; tone: "up" | "down" | "new" | "" } {
  if (b.status === "new") return { text: "New start", tone: "new" };
  if (b.status === "ending") return { text: "Ending", tone: "down" };
  if (b.change == null) return { text: "", tone: "" };
  const pct = Math.round(b.change * 100);
  if (!pct) return { text: "No change", tone: "" };
  return { text: `${pct > 0 ? "▲" : "▼"} ${Math.abs(pct)}%`, tone: pct > 0 ? "up" : "down" };
}

/** Highlighter for search and explanation terms: the server gives stemmed words; match surface forms
 * (C-UAS for cuas, UAVs for uav). Returns parts to render, marking the matches. */
export function highlightParts(text: string, terms: string[]): { text: string; mark: boolean }[] {
  const parts = terms
    .filter((t) => t && t.length > 1)
    .map((t) =>
      t
        .split(" ")
        .map((w) => {
          const e = w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const loose = !/\d/.test(w) && w.length <= 8 ? [...w].map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("[-/.]?") : e;
          return loose + (w.length >= 4 ? "\\w*" : "(?:s|es)?\\b");
        })
        .join("[\\W_]+"),
    );
  if (!parts.length) return [{ text, mark: false }];
  const re = new RegExp("\\b(?:" + parts.join("|") + ")", "gi");
  const out: { text: string; mark: boolean }[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    if (!m[0]) continue;
    out.push({ text: text.slice(last, m.index), mark: false }, { text: m[0], mark: true });
    last = (m.index ?? 0) + m[0].length;
  }
  out.push({ text: text.slice(last), mark: false });
  return out;
}

export const WORK_NAMES: Record<string, string> = {
  research: "Research & prototyping",
  development: "Engineering & software development",
  advisory: "Technical & advisory services",
  operations: "Operational & staffing services",
  care: "Health care & clinical services",
  sustainment: "Maintenance & sustainment",
  products: "Products, equipment & licenses",
  facilities: "Construction & facilities",
};
export const PATH_NAMES: Record<string, string> = {
  baa: "Research announcements (BAA)",
  ota: "CSO / OTA",
  sbir: "SBIR / STTR",
  prize: "Prizes & challenges",
  grant: "Grants",
  rfp: "Competitive RFPs",
  quote: "Quotes (RFQ)",
  vehicle: "Orders under existing contracts",
};

/** "a, b and c" from any items (text or elements). */
export function joinWords<T>(items: T[]): (T | string)[] {
  const out: (T | string)[] = [];
  items.forEach((item, i) => {
    if (i) out.push(i === items.length - 1 ? " and " : ", ");
    out.push(item);
  });
  return out;
}
